import { useContext } from 'react';
import { useMutation } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Form } from 'semantic-ui-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { SuccessfulAlert } from '../../../alerts/successAlerts';
import { ErrorAlert } from '../../../alerts/errorAlert';
import { WarningAlert } from '../../../alerts/WarningAlert';
import { AdminContext } from '../../../context/AdminContext/AdminContext';
import {
  GET_ALL_PAQUETES,
  GET_ALL_PRESTACIONES,
  CREATE_PAQUETE,
  UPDATE_PAQUETE,
} from '../../../Querys/prestaciones/querys';
import BenefitPickerList from './BenefitPickerList';
import '../css/ModalPrestacion.css';

interface PackageModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  packageData?: any;
  existingPackages: any[];
  allProjects: any[];
  allPrestaciones: any[];
  loadingPrestaciones?: boolean;
}

const validationSchema = Yup.object({
  nombre: Yup.string().required('Obligatorio').max(150, 'Máximo 150 caracteres'),
  idProject: Yup.string().required('Selecciona un proyecto'),
  idPrestaciones: Yup.array()
    .of(Yup.string())
    .min(1, 'Selecciona al menos una prestación'),
});

const PackageModal = ({
  open,
  onClose,
  mode,
  packageData,
  existingPackages,
  allProjects,
  allPrestaciones,
  loadingPrestaciones = false,
}: PackageModalProps) => {
  const { adminState } = useContext(AdminContext);
  const idUsuario = adminState?.id ?? '';

  const isEdit = mode === 'edit';

  const [createPaquete] = useMutation(CREATE_PAQUETE, {
    refetchQueries: [{ query: GET_ALL_PAQUETES }, { query: GET_ALL_PRESTACIONES }],
  });
  const [updatePaquete] = useMutation(UPDATE_PAQUETE, {
    refetchQueries: [{ query: GET_ALL_PAQUETES }, { query: GET_ALL_PRESTACIONES }],
  });

  const initialValues = {
    nombre: isEdit ? packageData?.nombre ?? '' : '',
    idProject: isEdit ? String(packageData?.idProject ?? '') : '',
    idPrestaciones: isEdit
      ? (packageData?.prestaciones ?? []).map((p: any) => String(p.id))
      : [] as string[],
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const input = {
          nombre: values.nombre.trim(),
          idProject: values.idProject,
          idPrestaciones: values.idPrestaciones,
          idUsuario,
        };
        if (isEdit) {
          const count = packageData?.countColaboradores ?? 0;
          if (count > 0) {
            const origNombre = (packageData?.nombre ?? '').trim();
            const origIdProject = String(packageData?.idProject ?? '');
            const origIds = [...(packageData?.prestaciones ?? []).map((p: any) => String(p.id))].sort().join(',');
            const newIds = [...values.idPrestaciones].sort().join(',');
            const hasChanges =
              values.nombre.trim() !== origNombre ||
              values.idProject !== origIdProject ||
              newIds !== origIds;
            if (hasChanges) {
              const confirm = await WarningAlert({
                text: `Este cambio afectará a ${count} colaborador${count === 1 ? '' : 'es'} asignado${count === 1 ? '' : 's'} a este paquete. ¿Deseas continuar?`,
                showDenyButton: true,
                confirmButtonText: 'Continuar',
              });
              if (!confirm?.isConfirmed) return;
            }
          }
          await updatePaquete({ variables: { id: packageData.id, input } });
          SuccessfulAlert({ text: 'Paquete actualizado correctamente.' });
        } else {
          await createPaquete({ variables: { input } });
          SuccessfulAlert({ text: 'Paquete creado correctamente.' });
        }
        resetForm();
        onClose();
      } catch (e: any) {
        const msg = e?.message?.includes('DUPLICATE_PACKAGE')
          ? 'Ya existe un paquete activo para este proyecto.'
          : `Ocurrió un error al ${isEdit ? 'actualizar' : 'crear'} el paquete.`;
        ErrorAlert({ text: msg });
      }
    },
  });

  // Advertencia de duplicado (verificación client-side, no bloqueante)
  const duplicateWarning = (() => {
    const { idProject } = formik.values;
    if (!idProject) return false;
    return existingPackages.some(
      (p) =>
        p.status !== false &&
        String(p.idProject) === idProject &&
        (!isEdit || String(p.id) !== String(packageData?.id))
    );
  })();

  const selectedProject = allProjects.find(
    (p) => String(p.id) === formik.values.idProject
  ) ?? null;

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="package-dialog-title"
      className="dialogPrestacion"
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <Form onSubmit={formik.handleSubmit} className="containerMdalPrestacion">
          <DialogTitle>
            <span className="titlePrestacion">
              {isEdit ? 'Editar paquete' : 'Nuevo paquete'}
            </span>
          </DialogTitle>

          {/* Nombre */}
          <DialogContent className="oneDivPrestacion">
            <TextField
              type="text"
              name="nombre"
              label="Nombre del paquete *"
              variant="outlined"
              size="small"
              style={{ width: 465, fontFamily: 'Roboto', fontStyle: 'italic' }}
              inputProps={{ maxLength: 150 }}
              value={formik.values.nombre}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              onKeyPress={(e) => { e.key === 'Enter' && e.preventDefault(); }}
              error={formik.touched.nombre && Boolean(formik.errors.nombre)}
              helperText={formik.touched.nombre && formik.errors.nombre}
            />
          </DialogContent>

          {/* Proyecto / Client — campo único */}
          <DialogContent className="oneDivPrestacion">
            <Autocomplete
              options={allProjects}
              getOptionLabel={(p: any) => `${p.proyectName} / ${p.client}`}
              value={selectedProject}
              onChange={(_, newVal: any) => {
                formik.setFieldValue('idProject', newVal ? String(newVal.id) : '');
              }}
              onBlur={() => formik.setFieldTouched('idProject', true)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proyecto / Cliente *"
                  variant="outlined"
                  size="small"
                  style={{ width: 465, fontFamily: 'Roboto', fontStyle: 'italic' }}
                  error={formik.touched.idProject && Boolean(formik.errors.idProject)}
                  helperText={formik.touched.idProject && formik.errors.idProject}
                />
              )}
              noOptionsText="Sin resultados"
            />
          </DialogContent>

          {/* Advertencia de duplicado */}
          {duplicateWarning && (
            <DialogContent className="oneDivPrestacion">
              <div style={{
                background: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 4,
                padding: '8px 12px',
                fontSize: 13,
                color: '#795548',
              }}>
                ⚠ Ya existe un paquete activo para este proyecto.
              </div>
            </DialogContent>
          )}


          {/* Prestaciones */}
          <DialogContent className="oneDivPrestacion">
            <div style={{ width: '100%' }}>
              <div style={{ marginBottom: 6, fontFamily: 'Roboto', fontSize: 13, color: '#555' }}>
                Prestaciones incluidas *
              </div>
              <BenefitPickerList
                allPrestaciones={allPrestaciones}
                selectedIds={formik.values.idPrestaciones as string[]}
                onChange={(ids) => formik.setFieldValue('idPrestaciones', ids)}
                filterByActive={true}
                loading={loadingPrestaciones}
              />
              {formik.touched.idPrestaciones && formik.errors.idPrestaciones && (
                <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                  {formik.errors.idPrestaciones as string}
                </div>
              )}
            </div>
          </DialogContent>

          {/* Botones */}
          <DialogContent className="twoBtnsPrestacion">
            <div className="containerButtons">
              <div className="containerCancelPreestacion">
                <button className="btnCancelPrestacion" type="button" onClick={handleClose}>
                  Cancelar
                </button>
              </div>
              <div className="containerAddPrestacion">
                <button className="btnAddPrestacion" type="submit">
                  <div className="infobtnAddRestriccion">
                    <div>
                      <span className="txtBtnAdd">
                        {isEdit ? 'Actualizar paquete' : 'Crear paquete'}
                      </span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </DialogContent>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PackageModal;
